<template>
    <div class="text-sm sm:text-base md:text-lg text-grey-darker flex justify-center">
        <HalfCircleSpinner
            v-show="!initiated"
            color="#0b5aaa"
            class="m-32"
        ></HalfCircleSpinner>
        <form-wizard
            v-if="initiated"
            shape="circle"
            :stepSize="deviceSize"
            color="#0b5aaa"
            error-color="#20a0ff"
            @on-loading="setLoading"
            @on-error="handleErrorMessage"
            transition="slide in"
            :start-index="wizardStartIndex"
            class="w-full"
        >
            <h2 slot="title"></h2>
            <tab-content
                title="Animal Type"
                icon="ti-view-grid"
                :before-change="validateAnimal"
            >
                <animal
                    :productGroup="currentMatrix.productGroup"
                    @animalSelected="setSelectedAnimal"
                ></animal>
            </tab-content>
            <tab-content
                title="Feed"
                icon="ti-view-list"
                :before-change="validatePhytate"
            >
                <feed-table
                    :animal="currentMatrix.productGroup"
                    :dietId="currentMatrix.diet && currentMatrix.diet.id ? currentMatrix.diet.id : undefined"
                    :diets="diets[currentMatrix.productGroup]"
                    :dietaryPhytate="currentMatrix.dietaryPhytate"
                    :ingredientsArray="ingredients"
                    @update:phytate="phytateUpdated"
                    @update:diet="data => currentMatrix.diet=data.diet"
                    @addItemToDiets="addItemToDiets"
                    @update:diets="data => formatDiets(data)"
                    @deleteLocalDiet="payload => deleteLocalDiet(payload.dietId)"
                    @phytateValueManual="value => phytateValueManual = value"
                ></feed-table>
            </tab-content>
            <tab-content
                title="RONOZYME HiPhos"
                icon="ti-arrow-circle-down"
                :before-change="validateProduct"
            >
                <product
                    :suggested-dose="suggestedDose"
                    @update:product="product => this.currentMatrix.productForm = product"
                ></product>
            </tab-content>
            <tab-content
                title="Phytate Accessibility Factor"
                icon="fa fa-percent"
                :before-change="calculateAccessibility"
            >
                <accessibility
                    :defaultValue="defaultAccessibility"
                    :selectedAnimal="currentMatrix.productGroup"
                    :totalPhytateP="currentMatrix.dietaryPhytate"
                    :user="user"
                ></accessibility>
            </tab-content>
            <tab-content
                title="DSM Nutrient Matrix"
                icon="ti-layout-grid4-alt"
            >
                <matrix
                    :rows="currentMatrix.nutrients"
                    :matrixId="currentMatrix.id"
                    :matrixName="currentMatrix.name"
                    :phytate="currentMatrix.dietaryPhytate"
                    :animal="currentMatrix.productGroup"
                    :accessibility="currentMatrix.accessibility"
                    :accessiblePhytateP="currentMatrix.accessiblePhytateP"
                    :correctedPhytateP="currentMatrix.correctedPhytateP"
                    :product="currentMatrix.productForm"
                    :dose="currentMatrix.dose"
                    :suggestedDose="currentMatrix.dietSuggestedDose"
                    :exportModalActive.sync="exportModalActive"
                    :exportWithProperties.sync="exportWithProperties"
                    :showSaveMatrixModal.sync="showSaveMatrixModal"
                    :dietName="currentMatrix != null && currentMatrix.diet != null ? currentMatrix.diet.name : null"
                    :phosphateType="currentMatrix != null ? currentMatrix.phosphateType : null"
                    :phosphatePrice="currentMatrix != null ? currentMatrix.phosphatePrice : null"
                    :hiphosPrice="currentMatrix != null ? currentMatrix.hiphosPrice : null"
                    :digestibility="currentMatrix != null ? currentMatrix.digestibility : null"
                    :availability="currentMatrix != null ? currentMatrix.availability : null"
                    :totalPhosphorus="currentMatrix != null ? currentMatrix.totalPhosphorus : null"
                    :currentMatrix="currentMatrix"
                    @update:matrixProps="handleMatrixPropsUpdate"
                ></matrix>
            </tab-content>
            <div
                class="column is-8 is-offset-2 notification is-danger has-text-centered"
                v-if="errorMsg"
            >
                {{errorMsg}}
            </div>
            <template
                slot="footer"
                slot-scope="props"
                class=""
            >
                <div class="mt-8 fixed pin-b pin-r pin-l bg-white py-2 px-2 md:px-4 shadow">
                    <div class="max-w-3xl mx-auto">
                        <div class=wizard-footer-left>
                            <wizard-button
                                v-if="props.activeTabIndex > 0"
                                @click.native="props.prevTab()"
                                :style="{ backgroundColor: '#0b5aaa', color: 'white', padding: '1rem 1.5rem', border: 'none' }"
                                class="btn rounded-full text-sm md:text-base"
                            >Previous</wizard-button>
                        </div>
                        <div class="wizard-footer-right">
                            <wizard-button
                                v-show="props.isLastStep"
                                @click.native="showSaveMatrixModal = true"
                                :style="{ backgroundColor: 'white', color: 'black', padding: '1rem 1.5rem', border: 'none' }"
                                class="btn bg-grey-lighter btn-blank rounded-full text-grey-darkest text-sm md:text-base "
                            >Save Calculation</wizard-button>

                            <wizard-button
                                v-if="!props.isLastStep"
                                @click.native="props.nextTab()"
                                :style="{ backgroundColor: '#0b5aaa', color: 'white', padding: '1rem 1.5rem', border: 'none' }"
                                class="btn rounded-full text-sm md:text-base"
                            >Next</wizard-button>

                            <wizard-button
                                v-else
                                @click.native="exportModalActive = true"
                                :style="{ backgroundColor: '#0b5aaa', color: 'white', padding: '1rem 1.5rem', border: 'none' }"
                                class="btn rounded-full text-sm md:text-base"
                            >Export...</wizard-button>
                        </div>
                    </div>
                </div>
            </template>
        </form-wizard>
    </div>
</template>

<script>
import { bugsnagClient } from "../lib/bugsnag";
import { HalfCircleSpinner } from "epic-spinners";
import { PRODUCT_FORM_MAP } from "../utilities/application";

export default {
    components: {
        HalfCircleSpinner
    },
    props: ["passedMatrixData", "user"],
    data: () => ({
        form: new Form({
            productGroup: "",
            dose: "",
            productForm: "HiPhos 10.000 GT",
            steps: [1]
        }),
        defaultAccessibility: 0.8,

        matrixData: [],
        submitted: false,
        loadingWizard: false,
        errorMsg: null,
        selectedFileFormat: null,
        exportModalActive: false,
        exportWithProperties: false,
        showSaveMatrixModal: false,
        currentMatrix: {
            id: 0,
            name: "",
            productForm: "HiPhos 10,000 GT",
            productGroup: "",
            dose: 0,
            doseInPPM: 0,
            dietSuggestedDose: 0,
            accessibility: 0.0,
            dietaryPhytate: "",
            accessiblePhytateP: 0.0,
            correctedPhytateP: 0.0,
            diet: {
                id: "",
                name: "",
                ingredients: []
            },
            nutrients: [],
            digestibility: 0,
            phosphateType: "",
            phosphatePrice: 0,
            hiphosPrice: 0,
            availability: 0,
            totalPhosphorus: 0
        },
        ingredients: [],
        diets: {},
        initiated: false,
        phytateValueManual: false
    }),
    mounted: function() {
        EventHub.$on("correctedPhytatePUpdated", correctedPhytateP => {
            this.currentMatrix.correctedPhytateP = correctedPhytateP;
        });
        EventHub.$on("accessiblePhytatePUpdated", accessiblePhytateP => {
            this.currentMatrix.accessiblePhytateP = accessiblePhytateP;
        });
        EventHub.$on(
            "correctedAccessibilityUpdated",
            correctedAccessibility => {
                this.currentMatrix.accessibility = correctedAccessibility;
            }
        );
        EventHub.$on("exportMatrix", payload => {
            this.exportMatrix(payload.matrixInPctg, payload.selectedUnit);
        });
        EventHub.$on("fileFormatUpdated", selectedFileFormat => {
            this.selectedFileFormat = selectedFileFormat;
        });
        EventHub.$on("saveMatrix", payload => {
            this.currentMatrix.name = payload.matrixName;
            if (payload.isNew || this.currentMatrix.id == 0) {
                this.createMatrix();
            } else {
                this.saveMatrix();
            }
        });
        EventHub.$on("saveDiet", this.handleSaveDiet);
        bugsnagClient.notify(new Error());
    },

    beforeCreate: async function() {
        await axios
            .get("/ingredients")
            .then(response => {
                this.ingredients = response.data;
            })
            .catch(error => {
                this.$toasted.global.calc_error({
                    error,
                    additionalMessage: "Please try refreshing the page."
                });
                bugsnagClient.notify(error);
            });

        await axios
            .get("/diets")
            .then(response => this.formatDiets(response.data.diets))
            .then(currentDietId => {})
            .catch(error => {
                this.$toasted.global.calc_error({
                    error,
                    additionalMessage: "Please try refreshing the page."
                });
                bugsnagClient.notify(error);
            });
        await this.init();
    },

    computed: {
        deviceSize() {
            if (this.$mq === "mobile") {
                return "xs";
            } else if (this.$mq === "tablet") {
                return "sm";
            } else {
                return "md";
            }
        },

        suggestedDose() {
            return _.round(this.currentMatrix.dose / 50, 0) * 50;
        },

        initialMatrixArray() {
            return this.passedMatrixData
                ? JSON.parse(this.passedMatrixData)
                : [];
        },

        wizardStartIndex() {
            return this.currentMatrix.id == 0 ? 0 : 4;
        }
    },
    methods: {
        init: async function() {
            if (this.initialMatrixArray.length == 0) {
                this.initiated = true;
                return;
            }
            try {
                this.currentMatrix = this.initialMatrixArray[0];
                this.currentMatrix.doseInPPM =
                    this.currentMatrix.dose &&
                    this.currentMatrix.productForm &&
                    PRODUCT_FORM_MAP[this.currentMatrix.productForm]
                        ? _.round(
                              (this.currentMatrix.dose /
                                  PRODUCT_FORM_MAP[
                                      this.currentMatrix.productForm
                                  ]) *
                                  1000,
                              2
                          )
                        : 0;
                this.currentMatrix.dietSuggestedDose = this.currentMatrix
                    .dietSuggestedDose
                    ? this.currentMatrix.dietSuggestedDose
                    : this.currentMatrix.dose;
                this.currentMatrix.nutrients = this.currentMatrix.nutrients.map(
                    row => {
                        return {
                            ...row,
                            contribution: row.pivot.contribution,
                            nutrient_id: row.pivot.nutrient_id,
                            unit: row.pivot.unit,
                            value: row.pivot.value
                        };
                    }
                );
                this.submitted = true;
                this.initiated = true;
            } catch (error) {
                this.$toasted.global.calc_error({
                    error,
                    additionalMessage: `Failed to load matrix calculation`
                });
                bugsnagClient.notify(error);
            }
            return;
        },
        setLoading: function(value) {
            this.loadingWizard = value;
        },
        handleErrorMessage: function(errorMsg) {
            this.errorMsg = errorMsg;
        },

        formatDiets(newDiets, currentDietId) {
            let groupedDiets = _.chain(newDiets)
                .map(diet => ({
                    ...diet,
                    id: _.toString(diet.id),
                    edited: false
                }))
                .groupBy("productGroup")
                .value();
            this.diets = _.mapValues(groupedDiets, animalDiets => {
                return _.keyBy(animalDiets, diet => _.toString(diet.id));
            });
            return Promise.resolve(currentDietId);
        },

        handleSaveDiet: async function() {
            if (
                this.currentMatrix.diet &&
                this.currentMatrix.diet.id &&
                !_.toString(this.currentMatrix.diet.id).includes("temp")
            ) {
                await this.saveDiet();
            } else {
                await this.createDiet();
            }
            return;
        },

        createDiet: async function() {
            var self = this;
            return axios
                .post("/createDiet", {
                    diet: self.currentMatrix.diet
                })
                .then(response =>
                    self.formatDiets(
                        response.data.diets,
                        response.data.currentDietId
                    )
                )
                .then(currentDietId => {
                    self.currentMatrix.diet =
                        self.diets[self.currentMatrix.productGroup][
                            currentDietId
                        ];
                    self.currentMatrix.diet.edited = false;
                    self.currentMatrix.diet_id = currentDietId;
                    self.$toasted.global.calc_success({
                        mainMessage: `Diet '${self.currentMatrix.diet.name}' saved successfully!`
                    });
                })
                .catch(error => {
                    self.$toasted.global.calc_error({
                        error,
                        mainMessage: `Diet '${self.currentMatrix.diet.name}' couldn not be saved.`,
                        additionalMessage: _.get(error, "response.data.message")
                            ? error.response.data.message
                            : ""
                    });
                    bugsnagClient.notify(error);
                });
        },

        saveDiet: async function() {
            var self = this;
            return axios
                .post("/saveDiet", {
                    diet: self.currentMatrix.diet
                })
                .then(response => {
                    self.$toasted.global.calc_success({
                        mainMessage: `Diet ${self.currentMatrix.diet.name} saved successfully!`
                    });
                    self.currentMatrix.diet.edited = false;
                })
                .catch(error => {
                    self.$toasted.global.calc_error({
                        error,
                        additionalMessage: `Diet ${self.currentMatrix.diet.name} couldn not be saved.`
                    });
                    bugsnagClient.notify(error);
                });
        },

        validateAnimal: function() {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    if (this.currentMatrix.productGroup == "") {
                        this.form.steps.push(2);
                        reject("Please select an animal type to proceed.");
                    } else {
                        if (this.currentMatrix.accessibility == 0) {
                            this.currentMatrix.accessibility = this.defaultAccessibility;
                        }
                        this.form.steps.push(3);
                        resolve(true);
                    }
                }, 500);
            });
        },
        validateProduct: function() {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    if (this.currentMatrix.productForm == "") {
                        EventHub.$emit("invalidProduct");
                        this.form.steps.push(3);
                        reject("");
                    } else {
                        this.form.steps.push(4);
                        resolve(true);
                    }
                }, 500);
            });
        },
        setSelectedAnimal: async function(selectedAnimal) {
            await this.resetCurrentMatrix();
            this.currentMatrix.productGroup = selectedAnimal;
            this.errorMsg = "";
            if (this.submitted && this.currentMatrix.correctedPhytateP) {
                this.calculateAccessibility();
            }
        },
        resetCurrentMatrix() {
            this.currentMatrix.diet = {
                id: "",
                name: "",
                ingredients: []
            };
            this.currentMatrix.dose = 0;
            this.currentMatrix.accessibility = 0.0;
            this.currentMatrix.dietaryPhytate = "";
            this.currentMatrix.correctedPhytateP = 0;
            this.currentMatrix.accessiblePhytateP = 0;
            this.currentMatrix.nutrients = [];
        },
        setPhytase: function() {
            this.currentMatrix.dietaryPhytate;
        },
        calculate: function() {
            axios
                .post("/matrix", {
                    productGroup: this.currentMatrix.productGroup,
                    productForm: this.currentMatrix.productForm,
                    dose: this.currentMatrix.dose,
                    digestibility: this.currentMatrix.digestibility
                })
                .then(
                    response => (this.currentMatrix.nutrients = response.data)
                )
                .catch(error => {
                    this.$toasted.global.calc_error({
                        error,
                        additionalMessage: `Matrix could not be calculated.`
                    });
                    bugsnagClient.notify(error);
                });

            this.submitted = true;
            return true;
        },
        recalculate: function() {
            if (this.submitted) {
                this.calculate();
            }

            return true;
        },
        phytateUpdated: function(data) {
            this.currentMatrix.dietaryPhytate = data.phytate;
            EventHub.$emit("phytateUpdated");
        },

        validatePhytate: function() {
            const _this = this;
            return new Promise((resolve, reject) => {
                if (_this.currentMatrix.diet && _this.currentMatrix.diet.id != "" && !_this.phytateValueManual) {
                    let pctgSum = _this.currentMatrix.diet.ingredients.reduce(
                        (pctg, ingr) => pctg + parseFloat(ingr.percentage),
                        0
                    );
                    if (pctgSum != 100) {
                        reject("Total inclusion percentage needs to be 100%.");
                    }
                }
                if (_this.currentMatrix.dietaryPhytate === "") {
                    reject(
                        "Please select a diet or enter your dietary Phytate-P value manually"
                    );
                } else if (_this.currentMatrix.dietaryPhytate === 0) {
                    reject(
                        "Phytate-P value is zero. Please add ingredients to your diet or enter a non-zero custom Phytate-P value."
                    );
                } else {
                    resolve(true);
                }
            });
        },

        calculateAccessibility: function() {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    axios
                        .post("/calculateDose", {
                            productGroup: this.currentMatrix.productGroup,
                            dietaryPhytate: this.currentMatrix.correctedPhytateP
                        })
                        .then(response => {
                            this.currentMatrix.dose = response.data;
                            EventHub.$emit("update:dose", {
                                dose: response.data
                            });
                            this.currentMatrix.dietSuggestedDose =
                                response.data;
                            this.calculate();
                            resolve(true);
                        })
                        .catch(error => {
                            this.$toasted.global.calc_error({
                                error,
                                additionalMessage: `Matrix could not be calculated.`
                            });
                            bugsnagClient.notify(error);
                            reject("Oops something went wrong");
                        });
                }, 500);
            });
        },

        createMatrix: async function() {
            if (this.currentMatrix.diet.edited) {
                await this.handleSaveDiet();
            }
            axios
                .post("/createMatrix", {
                    diet: this.currentMatrix.diet,
                    productGroup: this.currentMatrix.productGroup,
                    productForm: this.currentMatrix.productForm,
                    dietaryPhytate: this.currentMatrix.dietaryPhytate,
                    accessiblePhytateP: this.currentMatrix.accessiblePhytateP,
                    correctedPhytateP: this.currentMatrix.correctedPhytateP,
                    dose: this.currentMatrix.dose,
                    dietSuggestedDose: this.currentMatrix.dietSuggestedDose,
                    accessibility: this.currentMatrix.accessibility,
                    matrix: this.currentMatrix.nutrients,
                    name: this.currentMatrix.name
                })
                .then(response => {
                    this.currentMatrix.id = response.data.matrixId;
                    this.currentMatrix.name = response.data.matrixName;
                    this.$toasted.global.calc_success({
                        mainMessage: `Calculation '${this.currentMatrix.name}' created successfully!`
                    });
                })
                .catch(error => {
                    this.$toasted.global.calc_error({
                        error,
                        additionalMessage: `Calculation '${this.currentMatrix.name}' couldn not be created. `
                    });
                    bugsnagClient.notify(error);
                });
        },

        saveMatrix: async function() {
            if (this.currentMatrix.diet.edited) {
                await this.handleSaveDiet();
            }
            axios
                .post("/saveMatrix", {
                    diet: this.currentMatrix.diet,
                    productGroup: this.currentMatrix.productGroup,
                    productForm: this.currentMatrix.productForm,
                    dietaryPhytate: this.currentMatrix.dietaryPhytate,
                    accessiblePhytateP: this.currentMatrix.accessiblePhytateP,
                    correctedPhytateP: this.currentMatrix.correctedPhytateP,
                    dose: this.currentMatrix.dose,
                    dietSuggestedDose: this.currentMatrix.dietSuggestedDose,
                    accessibility: this.currentMatrix.accessibility,
                    matrix: this.currentMatrix.nutrients,
                    name: this.currentMatrix.name,
                    id: this.currentMatrix.id
                })
                .then(response => {
                    this.$toasted.global.calc_success({
                        mainMessage: `Calculation '${this.currentMatrix.name}' saved successfully!`
                    });
                })
                .catch(error => {
                    this.$toasted.global.calc_error({
                        error,
                        additionalMessage: `Calculation '${this.currentMatrix.name}' couldn not be saved.`
                    });
                    bugsnagClient.notify(error);
                });
        },

        exportMatrix: function(matrixInPctg, selectedUnit) {
            if (this.submitted) {
                axios({
                    method: "post",
                    url: "/exportMatrix",
                    data: {
                        matrix_data:
                            selectedUnit == "%"
                                ? matrixInPctg
                                : this.currentMatrix.nutrients,
                        fileFormat: this.selectedFileFormat,
                        withProperties: this.exportWithProperties,
                        properties: {
                            phytate: this.currentMatrix.dietaryPhytate,
                            animal: this.currentMatrix.productGroup,
                            accessibility: this.currentMatrix.accessibility,
                            accessiblePhytateP: this.currentMatrix
                                .accessiblePhytateP,
                            correctedPhytateP: this.currentMatrix
                                .correctedPhytateP,
                            product: this.currentMatrix.productForm,
                            dose: this.currentMatrix.dose,
                            dietSuggestedDose: this.currentMatrix
                                .dietSuggestedDose,
                            doseInPPM: this.currentMatrix.doseInPPM,
                            dietName: this.currentMatrix.diet != null ? this.currentMatrix.diet.name : null
                        }
                    },
                    responseType: "blob",
                    headers: {
                        Accept:
                            "text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    }
                })
                    .then(response => {
                        let blob = new Blob([response.data]);
                        if (!window.navigator.msSaveOrOpenBlob) {
                            var blobURL = window.URL.createObjectURL(
                                new Blob([response.data])
                            );
                            var tempLink = document.createElement("a");
                            tempLink.style.display = "none";
                            tempLink.href = blobURL;
                            tempLink.setAttribute(
                                "download",
                                `matrix.${this.selectedFileFormat}`
                            );

                            // Safari thinks _blank anchor are pop ups. We only want to set _blank
                            // target if the browser does not support the HTML5 download attribute.
                            // This allows you to download files in desktop safari if pop up blocking
                            // is enabled.
                            if (typeof tempLink.download === "undefined") {
                                tempLink.setAttribute("target", "_blank");
                            }

                            document.body.appendChild(tempLink);
                            tempLink.click();
                            document.body.removeChild(tempLink);
                            window.URL.revokeObjectURL(blobURL);
                        } else {
                            window.navigator.msSaveOrOpenBlob(
                                new Blob([response.data]),
                                `matrix.${this.selectedFileFormat}`
                            );
                        }
                    })
                    .catch(function(error) {
                        bugsnagClient.notify(error);
                    });
            }
        },

        handleMatrixPropsUpdate(payload) {
            if (payload.type == "phytate") {
                this.currentMatrix.dietaryPhytate = payload.value;
                this.currentMatrix.accessiblePhytateP =
                    payload.value * this.defaultAccessibility;
                this.currentMatrix.correctedPhytateP =
                    payload.value * this.currentMatrix.accessibility;
                this.calculateAccessibility()
                    .then(data => {})
                    .catch(error => {
                        bugsnagClient.notify(error);
                    });
            } else if (payload.type == "dose" && payload.value != 0) {
                this.currentMatrix.dose = payload.doseInFYT;
                this.currentMatrix.doseInPPM = payload.doseInPPM;
                EventHub.$emit("update:dose", {
                    dose: payload.doseInFYT
                });
                this.recalculate();
            } else if (payload.type == "animal") {
                this.setSelectedAnimal(payload.value);
            } else if (payload.type == "product") {
                this.currentMatrix.productForm = payload.value;
                this.recalculate();
            } else if (payload.type == "digestibility") {
                this.currentMatrix.digestibility = payload.value;
                this.recalculate();
            } else {
                this.currentMatrix[payload.type] = payload.value;
            }
        },

        addItemToDiets(diet) {
            if (this.diets[diet.productGroup]) {
                this.diets[diet.productGroup][diet.id] = diet;
            } else {
                this.diets[diet.productGroup] = {};
                this.diets[diet.productGroup][diet.id] = diet;
            }
        },

        deleteLocalDiet(dietId) {
            if (_.has(this.diets, [this.currentMatrix.productGroup, dietId])) {
                delete this.diets[this.currentMatrix.productGroup][dietId];
            }
        }
    }
};
</script>
<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to
/* .fade-leave-active below version 2.1.8 */

 {
    opacity: 0;
}
</style>
